import moment from 'moment';
import {validateCPF} from '../../../../utils/index.js';

/*
 * Regras puras do cadastro de Processo de Seleção do administrativo
 * (migração do processoSeletivo/_form.xhtml do PrimeFaces): o que cada
 * programa exibe, duplicidades das listas internas (cursos, ENEM, processos
 * vestibulares e candidatos da carga), CPF, datas, grupos do formulário,
 * estado inicial do formulário e o corpo enviado no POST/PUT.
 */

/* Rota da lista no administrativo (a criação é /novo e a edição /:id). */
export const ROTA_PROCESSOS_SELECAO = '/administrativo/cadastros/processos-selecao';

/* Programas ofertados (ids da tabela de programas). */
export const PROGRAMAS = Object.freeze({
    INGRESSANTE: 1,
    VETERANO: 2,
    PROUNI: 3,
    FIES: 4,
    COLEGIO_TECNICO: 5,
    STRICTU_SENSU: 6
});

export const NOMES_DO_PROGRAMA = Object.freeze(['PROUNI', 'FIES', 'PBEST', 'REVALIDAÇÃO', 'CEBAS']);

export const SEMESTRES = Object.freeze([
    {valor: '1', descricao: '1º semestre'},
    {valor: '2', descricao: '2º semestre'}
]);

export const FORMATO_DATA_WIRE = 'YYYY-MM-DD';
export const FORMATO_DATA_HORA_WIRE = 'YYYY-MM-DDTHH:mm:ss';
export const FORMATO_DATA_TELA = 'DD/MM/YYYY';
export const FORMATO_DATA_HORA_TELA = 'DD/MM/YYYY HH:mm';

export const MENSAGEM_COLEGIO_UM_PROCESSO =
    'Para o processo de seleção do colégio tecnológico só é permitido a seleção de um processo seletivo permitido.';
export const MENSAGEM_CURSO_DUPLICADO = 'Este curso já foi adicionado.';
export const MENSAGEM_ENEM_DUPLICADO = 'O Enem já existe neste processo seletivo.';
export const MENSAGEM_PROCESSO_VESTIBULAR_DUPLICADO = 'Este processo seletivo já foi adicionado.';
export const MENSAGEM_PROCESSO_VESTIBULAR_NAO_SELECIONADO = 'Selecione um processo seletivo.';

// Formatos aceitos ao ler datas do back (data pura ou data e hora).
const FORMATOS_LEITURA = [FORMATO_DATA_HORA_WIRE, FORMATO_DATA_WIRE];

const vazio = valor => valor === null || valor === undefined || String(valor).trim() === '';
const lista = valor => (Array.isArray(valor) ? valor : []);
const mesmoValor = (a, b) => String(a ?? '') === String(b ?? '');

/* Situação do processo pela Data início/término do processo seletivo (calculada no back): opções do filtro da lista. */
export const OPCOES_SITUACAO_PROCESSO = Object.freeze([
    Object.freeze({value: 'ABERTO', label: 'Aberto'}),
    Object.freeze({value: 'AGUARDANDO', label: 'Aguardando início'}),
    Object.freeze({value: 'ENCERRADO', label: 'Encerrado'}),
    Object.freeze({value: 'SEM_PERIODO', label: 'Sem período'})
]);

/* Situação da inscrição pelo calendário "Inscrição Processo Seletivo" (calculada no back): opções do filtro da lista. */
export const OPCOES_SITUACAO_INSCRICAO = Object.freeze([
    Object.freeze({value: 'ABERTO', label: 'Aberta'}),
    Object.freeze({value: 'ENCERRADO', label: 'Encerrada'}),
    Object.freeze({value: 'SEM_PERIODO', label: 'Sem calendário'})
]);

/* Ano e semestre de referência como "2026/1"; sem semestre, só o ano; sem ano, vazio. */
export const formatarAnoSemestre = (ano, semestre) => {
    if (vazio(ano)) return '';
    return vazio(semestre) ? String(ano) : `${ano}/${semestre}`;
};

/* Id do programa como número; vazio ou inválido vira null. */
export const idDoPrograma = valor => {
    if (vazio(valor)) return null;
    const numero = Number(valor);
    return Number.isInteger(numero) ? numero : null;
};

/*
 * O que a tela exibe para o programa (regras do rendered do _form.xhtml):
 * - aviso de inscrição no site do governo para PROUNI e FIES;
 * - datas, termo de ciência e cursos só para programa < 3 ou Colégio Técnico;
 * - processos vestibulares para Ingressante (vários) e Colégio Técnico (um só);
 * - ENEM só para Ingressante.
 */
export const regrasDoPrograma = valor => {
    const id = idDoPrograma(valor);
    const selecionado = id !== null;
    return {
        programaSelecionado: selecionado,
        avisoInscricaoGoverno: id === PROGRAMAS.PROUNI || id === PROGRAMAS.FIES,
        exibirInscricao: selecionado && (id < PROGRAMAS.PROUNI || id === PROGRAMAS.COLEGIO_TECNICO),
        exibirProcessosVestibular: id === PROGRAMAS.INGRESSANTE || id === PROGRAMAS.COLEGIO_TECNICO,
        processoVestibularUnico: id === PROGRAMAS.COLEGIO_TECNICO,
        exibirEnems: id === PROGRAMAS.INGRESSANTE
    };
};

/* Rótulo da lista de processos vestibulares: plural para o Ingressante, singular para o Colégio Técnico. */
export const rotuloProcessosVestibular = valor => (regrasDoPrograma(valor).processoVestibularUnico
    ? 'Processo Seletivo Permitido'
    : 'Processos Seletivos Permitidos');

/* Ao trocar o programa para o Colégio Técnico, mais de um processo vestibular deixa de valer (como no carregarCursos do JSF). */
export const processosAoTrocarPrograma = (processosVestibular, novoPrograma) => {
    const atuais = lista(processosVestibular);
    return regrasDoPrograma(novoPrograma).processoVestibularUnico && atuais.length > 1 ? [] : atuais;
};

/* Ids dos processos vestibulares da lista, no formato "1,2" do parâmetro de cursos. */
export const idsProcessosVestibular = processosVestibular => lista(processosVestibular)
    .map(item => item?.idProcessoVestibular)
    .filter(id => !vazio(id))
    .join(',');

/* ---- Duplicidades das listas internas ---- */

const idDe = objeto => objeto?.id ?? null;

/* Mesma vaga: mesmo campus, curso e turno (sem turno = EAD). */
export const mesmaVaga = (a, b) => !!a && !!b
    && mesmoValor(idDe(a.campus), idDe(b.campus))
    && mesmoValor(idDe(a.curso), idDe(b.curso))
    && mesmoValor(idDe(a.turno), idDe(b.turno));

/* Vaga igual a outra da lista; `indiceIgnorado` é a própria vaga em edição. */
export const vagaDuplicada = (vagas, vaga, indiceIgnorado = -1) =>
    lista(vagas).some((item, indice) => indice !== indiceIgnorado && mesmaVaga(item, vaga));

/* ENEM do mesmo ano na lista; `indiceIgnorado` é o próprio ENEM em edição. */
export const enemDuplicado = (enems, enem, indiceIgnorado = -1) => !vazio(enem?.ano)
    && lista(enems).some((item, indice) => indice !== indiceIgnorado && mesmoValor(item?.ano, enem.ano));

/* Grava um item na lista (vaga, ENEM, calendário): substitui só o item em edição (indice > -1) ou acrescenta no fim. */
export const salvarItemNaLista = (itens, item, indice = -1) => {
    const atual = [...lista(itens)];
    if (indice > -1 && indice < atual.length) {
        atual[indice] = item;
    } else {
        atual.push(item);
    }
    return atual;
};

/* Processo vestibular (do apoio) no formato da lista do processo de seleção. */
export const paraProcessoVestibularDoProcesso = processo => ({
    id: null,
    idProcessoVestibular: processo?.id ?? null,
    descricao: processo?.descricao ?? '',
    tipo: processo?.tipo ?? null
});

/* Tipo do processo vestibular como texto (o apoio pode mandar texto ou {descricao}). */
export const descricaoTipoProcessoVestibular = tipo => (tipo && typeof tipo === 'object' ? (tipo.descricao ?? '') : (tipo ?? ''));

export const processoVestibularJaAdicionado = (processosVestibular, processo) => !!processo
    && lista(processosVestibular).some(item => mesmoValor(item?.idProcessoVestibular, processo.id));

/* Mensagem que impede adicionar o processo vestibular escolhido, ou null quando pode adicionar. */
export const validarProcessoVestibular = (processosVestibular, processo, idPrograma) => {
    if (!processo) return MENSAGEM_PROCESSO_VESTIBULAR_NAO_SELECIONADO;
    if (processoVestibularJaAdicionado(processosVestibular, processo)) return MENSAGEM_PROCESSO_VESTIBULAR_DUPLICADO;
    if (regrasDoPrograma(idPrograma).processoVestibularUnico && lista(processosVestibular).length > 0) return MENSAGEM_COLEGIO_UM_PROCESSO;
    return null;
};

/* ---- CPF e carga de candidatos ---- */

export const somenteDigitos = valor => String(valor ?? '').replace(/\D/g, '');

/* Sem formatação e com zeros à esquerda até 11 dígitos (normalizarCpf do ProcessoSeletivoCargaBean). */
export const normalizarCpf = valor => {
    const digitos = somenteDigitos(valor);
    return digitos ? digitos.padStart(11, '0') : '';
};

export const cpfValido = valor => {
    const cpf = normalizarCpf(valor);
    return cpf.length === 11 && validateCPF(cpf);
};

export const formatarCpf = valor => {
    const cpf = normalizarCpf(valor);
    return cpf.length === 11
        ? `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`
        : String(valor ?? '');
};

export const mensagemCpfDuplicado = cpf => `CPF já informado nesta carga: ${formatarCpf(cpf)}`;

/* CPF já presente na carga; `indiceIgnorado` é o próprio candidato. */
export const cpfDuplicado = (cargas, cpf, indiceIgnorado = -1) => {
    const procurado = normalizarCpf(cpf);
    return !!procurado && lista(cargas).some((item, indice) => indice !== indiceIgnorado && normalizarCpf(item?.cpf) === procurado);
};

/*
 * Acrescenta os candidatos importados à carga: os CPFs que já estão na carga
 * ficam de fora e voltam em `duplicados` (formatados) para o aviso. CPF repetido
 * dentro da própria planilha o back já recusa (400), como no JSF.
 */
export const mesclarCargas = (atuais, novas) => {
    const cargas = [...lista(atuais)];
    const duplicados = [];
    lista(novas).forEach(nova => {
        const cpf = normalizarCpf(nova?.cpf);
        if (cpfDuplicado(cargas, cpf)) {
            duplicados.push(formatarCpf(cpf));
            return;
        }
        cargas.push({...nova, id: nova?.id ?? null, cpf});
    });
    return {cargas, adicionadas: cargas.length - lista(atuais).length, duplicados};
};

/* Filtro da coluna CPF: aceita o termo com ou sem pontuação. */
export const filtroCpf = (termo, cpf) => {
    const digitos = somenteDigitos(termo);
    if (!digitos) return String(termo ?? '').trim() === '';
    return normalizarCpf(cpf).includes(digitos);
};

/* ---- Datas ---- */

/* Data no formato do back (`formato`), estrita. */
export const dataWireValida = (valor, formato) => !vazio(valor) && moment(String(valor), formato, true).isValid();

const lerData = valor => (vazio(valor) ? null : moment(String(valor), FORMATOS_LEITURA, true));

/* Data do back (pura ou com hora) reescrita no `formato`; vazia ou inválida vira ''. */
export const normalizarData = (valor, formato) => {
    const data = lerData(valor);
    return data && data.isValid() ? data.format(formato) : '';
};

const formatarParaTela = (valor, formato) => {
    if (vazio(valor)) return '-';
    const data = lerData(valor);
    return data && data.isValid() ? data.format(formato) : String(valor);
};

/* "dd/mm/aaaa" para as tabelas ("-" quando vazia). */
export const formatarData = valor => formatarParaTela(valor, FORMATO_DATA_TELA);

/* "dd/mm/aaaa hh:mm" para as tabelas ("-" quando vazia). */
export const formatarDataHora = valor => formatarParaTela(valor, FORMATO_DATA_HORA_TELA);

/* Valor do formulário (texto no formato do back) para o picker: null quando vazio. */
export const paraPicker = (valor, formato) => (vazio(valor) ? null : moment(String(valor), formato, true));

/*
 * Valor do picker para o formulário: a data válida no formato do back; a
 * digitação incompleta fica como texto (a validação acusa "Data inválida").
 */
export const doPicker = (data, texto, formato) => {
    if (data && moment.isMoment(data) && data.isValid()) return data.format(formato);
    return vazio(texto) ? '' : String(texto);
};

/* `inicio` estritamente antes de `fim` (ambos no `formato`); datas inválidas não são comparadas. */
export const dataAntes = (inicio, fim, formato) => !dataWireValida(inicio, formato) || !dataWireValida(fim, formato)
    || moment(inicio, formato, true).isBefore(moment(fim, formato, true));

/* `inicio` no mesmo dia ou antes de `fim`. */
export const dataAteh = (inicio, fim, formato) => !dataWireValida(inicio, formato) || !dataWireValida(fim, formato)
    || moment(inicio, formato, true).isSameOrBefore(moment(fim, formato, true));

/* Data de nascimento não pode ser futura (validar do ProcessoSeletivoCargaBean). */
export const dataFutura = (valor, hoje = moment()) => dataWireValida(valor, FORMATO_DATA_WIRE)
    && moment(valor, FORMATO_DATA_WIRE, true).isAfter(moment(hoje).endOf('day'));

/* ---- Formulário, ficha e documentos ---- */

/* Grupos do formulário separados em sem e com veterano, guardando a posição na lista original. */
export const separarConfiguracoesFormulario = configuracoes => lista(configuracoes).reduce((grupos, configuracao, indice) => {
    (configuracao?.veterano ? grupos.comVeterano : grupos.semVeterano).push({...configuracao, indice});
    return grupos;
}, {semVeterano: [], comVeterano: []});

/* Marca/desmarca "Exibir": sem exibir, o grupo deixa de ser obrigatório. */
export const alterarExibir = (configuracao, exibir) => ({
    ...configuracao,
    exibir: !!exibir,
    obrigatorio: exibir ? !!configuracao?.obrigatorio : false
});

/* Marca/desmarca "Obrigatório": só vale para grupo exibido. */
export const alterarObrigatorio = (configuracao, obrigatorio) => ({
    ...configuracao,
    obrigatorio: !!configuracao?.exibir && !!obrigatorio
});

const porOrdemENome = (a, b) => {
    const ordemA = Number.isFinite(Number(a?.ordem)) && !vazio(a?.ordem) ? Number(a.ordem) : Number.MAX_SAFE_INTEGER;
    const ordemB = Number.isFinite(Number(b?.ordem)) && !vazio(b?.ordem) ? Number(b.ordem) : Number.MAX_SAFE_INTEGER;
    return ordemA !== ordemB ? ordemA - ordemB : String(a?.nome ?? '').localeCompare(String(b?.nome ?? ''), 'pt-BR');
};

/* Documentos que ainda não estão selecionados, pela ordem do cadastro. */
export const documentosDisponiveis = (todos, selecionados) => lista(todos)
    .filter(documento => !lista(selecionados).some(selecionado => mesmoValor(selecionado?.id, documento?.id)))
    .sort(porOrdemENome);

/* ---- Estado do formulário e corpo do POST/PUT ---- */

const texto = valor => (valor === null || valor === undefined ? '' : String(valor));
const simNao = valor => {
    if (valor === true || valor === 'true') return 'true';
    if (valor === false || valor === 'false') return 'false';
    return '';
};

/* VO do back (GET /novo ou /{id}) → valores do formulário (selects e radios como texto). */
export const montarEstadoInicial = (vo = {}) => ({
    id: vo?.id ?? null,
    idPrograma: texto(vo?.programa?.id),
    descricao: texto(vo?.descricao),
    link: texto(vo?.link),
    numeroEdital: texto(vo?.numeroEdital),
    anoVigente: texto(vo?.anoVigente),
    semestreReferencia: texto(vo?.semestreReferencia),
    nomeDoPrograma: texto(vo?.nomeDoPrograma),
    dataEdital: normalizarData(vo?.dataEdital, FORMATO_DATA_WIRE),
    dataInicial: normalizarData(vo?.dataInicial, FORMATO_DATA_HORA_WIRE),
    dataFinal: normalizarData(vo?.dataFinal, FORMATO_DATA_HORA_WIRE),
    dataFinalAtualizacaoResultado: normalizarData(vo?.dataFinalAtualizacaoResultado, FORMATO_DATA_WIRE),
    termoCiencia: texto(vo?.termoCiencia),
    informacaoAdicional: texto(vo?.informacaoAdicional),
    processosVestibular: lista(vo?.processosVestibular),
    vagas: lista(vo?.vagas),
    enems: lista(vo?.enems),
    disponivelParaFuncionario: simNao(vo?.disponivelParaFuncionario),
    flagRevalidacao: simNao(vo?.flagRevalidacao),
    flagSomenteInscricaoComCargas: simNao(vo?.flagSomenteInscricaoComCargas),
    flagProtocoloEntrega: simNao(vo?.flagProtocoloEntrega),
    dataInicialAgendamento: normalizarData(vo?.dataInicialAgendamento, FORMATO_DATA_HORA_WIRE),
    dataFinalAgendamento: normalizarData(vo?.dataFinalAgendamento, FORMATO_DATA_HORA_WIRE),
    calendarios: lista(vo?.calendarios).map(calendario => ({
        ...calendario,
        dataInicial: normalizarData(calendario?.dataInicial, FORMATO_DATA_WIRE),
        dataFinal: normalizarData(calendario?.dataFinal, FORMATO_DATA_WIRE)
    })),
    documentos: lista(vo?.documentos).map(documento => ({id: documento?.id ?? null, nome: documento?.nome ?? ''})),
    email: {
        idCampus: texto(vo?.email?.idCampus),
        email: texto(vo?.email?.email)
    },
    configuracoesFormulario: lista(vo?.configuracoesFormulario),
    configuracoesFicha: lista(vo?.configuracoesFicha),
    flagInscricaoCargaPropria: simNao(vo?.flagInscricaoCargaPropria),
    cargas: lista(vo?.cargas).map(carga => ({
        ...carga,
        cpf: normalizarCpf(carga?.cpf),
        dataNascimento: normalizarData(carga?.dataNascimento, FORMATO_DATA_WIRE)
    }))
});

const textoOuNulo = valor => (vazio(valor) ? null : String(valor).trim());
const inteiroOuNulo = valor => {
    if (vazio(valor)) return null;
    const numero = Number(String(valor).trim());
    return Number.isInteger(numero) ? numero : null;
};
const booleanoOuNulo = valor => {
    if (valor === true || valor === 'true') return true;
    if (valor === false || valor === 'false') return false;
    return null;
};
const dataOuNula = (valor, formato) => (dataWireValida(valor, formato) ? String(valor) : null);

/*
 * Valores do formulário → VO do POST/PUT, com `idsDocumentos` (ids dos
 * documentos selecionados). As listas vão como estão, mesmo as que o programa
 * não exibe — como no JSF, que mantinha o que já estava no processo.
 */
export const montarPayload = (valores = {}, {programas = []} = {}) => {
    const idPrograma = idDoPrograma(valores.idPrograma);
    const programa = idPrograma === null
        ? null
        : {id: idPrograma, descricao: lista(programas).find(item => mesmoValor(item?.id, idPrograma))?.descricao ?? null};
    const documentos = lista(valores.documentos).map(documento => ({id: documento.id, nome: documento.nome}));

    return {
        id: valores.id ?? null,
        programa,
        descricao: textoOuNulo(valores.descricao),
        link: textoOuNulo(valores.link),
        numeroEdital: inteiroOuNulo(valores.numeroEdital),
        anoVigente: inteiroOuNulo(valores.anoVigente),
        semestreReferencia: inteiroOuNulo(valores.semestreReferencia),
        nomeDoPrograma: textoOuNulo(valores.nomeDoPrograma),
        dataEdital: dataOuNula(valores.dataEdital, FORMATO_DATA_WIRE),
        dataInicial: dataOuNula(valores.dataInicial, FORMATO_DATA_HORA_WIRE),
        dataFinal: dataOuNula(valores.dataFinal, FORMATO_DATA_HORA_WIRE),
        dataFinalAtualizacaoResultado: dataOuNula(valores.dataFinalAtualizacaoResultado, FORMATO_DATA_WIRE),
        termoCiencia: textoOuNulo(valores.termoCiencia),
        informacaoAdicional: textoOuNulo(valores.informacaoAdicional),
        processosVestibular: lista(valores.processosVestibular),
        vagas: lista(valores.vagas),
        enems: lista(valores.enems),
        disponivelParaFuncionario: booleanoOuNulo(valores.disponivelParaFuncionario),
        flagRevalidacao: booleanoOuNulo(valores.flagRevalidacao),
        flagSomenteInscricaoComCargas: booleanoOuNulo(valores.flagSomenteInscricaoComCargas),
        flagProtocoloEntrega: booleanoOuNulo(valores.flagProtocoloEntrega),
        dataInicialAgendamento: dataOuNula(valores.dataInicialAgendamento, FORMATO_DATA_HORA_WIRE),
        dataFinalAgendamento: dataOuNula(valores.dataFinalAgendamento, FORMATO_DATA_HORA_WIRE),
        calendarios: lista(valores.calendarios).map(calendario => ({
            id: calendario.id ?? null,
            idTipo: inteiroOuNulo(calendario.idTipo),
            descricaoTipo: calendario.descricaoTipo ?? null,
            dataInicial: dataOuNula(calendario.dataInicial, FORMATO_DATA_WIRE),
            dataFinal: dataOuNula(calendario.dataFinal, FORMATO_DATA_WIRE)
        })),
        documentos,
        idsDocumentos: documentos.map(documento => documento.id),
        email: {
            idCampus: inteiroOuNulo(valores.email?.idCampus),
            email: textoOuNulo(valores.email?.email)
        },
        configuracoesFormulario: lista(valores.configuracoesFormulario).map(configuracao => ({
            ...configuracao,
            exibir: !!configuracao.exibir,
            obrigatorio: !!configuracao.exibir && !!configuracao.obrigatorio
        })),
        configuracoesFicha: lista(valores.configuracoesFicha).map(configuracao => ({...configuracao, exibir: !!configuracao.exibir})),
        flagInscricaoCargaPropria: booleanoOuNulo(valores.flagInscricaoCargaPropria),
        cargas: lista(valores.cargas).map(carga => ({
            id: carga.id ?? null,
            nome: textoOuNulo(carga.nome),
            cpf: normalizarCpf(carga.cpf),
            codigoInscrito: textoOuNulo(carga.codigoInscrito),
            dataNascimento: dataOuNula(carga.dataNascimento, FORMATO_DATA_WIRE)
        }))
    };
};
